import { Client } from '@df/core/service/client';
import each from 'lodash-es/each';
import { NDfs } from '../dfs.interfaces';
import { dfsDimension2String } from '../util/dfs-dimension-2-string';
import { DfsPerSizeNormalizeService } from '../util/dfs-per-size-normalize';
import { dfsString2Dimension } from '../util/dfs-string-2-dimension';
import { DfsCssService } from './dfs-css.service';

export class DfsGlobalsService {
    /**
     * Distance quant
     */
    private distanceQuant: NDfs.TPerSize<NDfs.IDimension> | NDfs.IDimension;

    /**
     * Scope attribute
     */
    readonly dfsScopeAttr = this.dfsCssService.getScopeAttr(this.dfsOptions.scope);

    /**
     * Dependencies
     */
    constructor(
        private dfsOptions: NDfs.IOptions,
        private client: Client,
        private dfsCssService: DfsCssService,
        private dfsPerSizeNormalizeService: DfsPerSizeNormalizeService,
        private document: Document
    ) {
        const quant = this.dfsOptions.distanceQuant;
        this.distanceQuant = Array.isArray(quant)
            ? (quant.map(dfsString2Dimension) as NDfs.TPerSize<NDfs.IDimension>)
            : dfsString2Dimension(quant);
        this.client.obs.breakpoint.subscribe(() => {
            this.updateDimensions();
        });
        this.updateDimensions();
        this.updateStatics();
        this.dfsCssService.applyVariableChanges();
    }

    private updateDimensions() {
        const sizeId = this.client.sizeId;
        const quant = this.dfsPerSizeNormalizeService.execute(this.distanceQuant, sizeId)!;

        const layout = this.dfsOptions.layout;
        const gutterDim = dfsString2Dimension(this.dfsPerSizeNormalizeService.execute(layout.gridGutter, sizeId)!);

        const allGuttersWidth = dfsDimension2String({
            value: gutterDim.value * (layout.gridSize - 1),
            unit: gutterDim.unit
        });
        this.dfsCssService.setVariable(this.dfsOptions.scope, '--dfs-quant', dfsDimension2String(quant!), false);
        this.dfsCssService.setVariable(this.dfsOptions.scope, '--dfs-gutter', dfsDimension2String(gutterDim!), false);

        // wrappers sizes
        each(this.dfsOptions.layout.wrap, (definition, id) => {
            const paddingDim = dfsString2Dimension(this.dfsPerSizeNormalizeService.execute(definition.padding, sizeId)!);
            const widthWithPadding = `calc(100vw - ${dfsDimension2String({ value: paddingDim.value * 2, unit: paddingDim.unit })})`;
            const wrapperWidth = dfsString2Dimension(this.dfsPerSizeNormalizeService.execute(definition.width, sizeId)!);
            const inWrapperWidth = wrapperWidth.value ? `min(${widthWithPadding}, ${dfsDimension2String(wrapperWidth)})` : widthWithPadding;

            this.dfsCssService.setVariable(this.dfsOptions.scope, `--dfs-wrap-${id}`, inWrapperWidth, false);
        });

        for (let i = 1; i <= layout.gridSize; i++) {
            const colWIdth = `(--dfs-wrap-default - ${allGuttersWidth}) / ${layout.gridSize}`;
            const width = i > 1 ? `calc((${i} * ${colWIdth} + ${i - 1}*var(--dfs-gutter)) - 0.0001px)` : `calc((${colWIdth}) - 0.0001px)`;
            this.dfsCssService.setVariable(this.dfsOptions.scope, `--dfs-${i}col`, width, false);
        }
    }

    private updateStatics() {
        each(['color'], property => {
            each(this.dfsOptions[property], (definition, id) => {
                this.dfsCssService.setVariable(this.dfsOptions.scope, this.getVar(property, id, false), definition.value, false);
            });
        });
    }

    getVar(property: string, id: string, existingOnly = true) {
        if (existingOnly) {
            if (!this.dfsOptions[property]) {
                throw new Error('Invalid dfs config requested ' + property);
            }
            const source = this.dfsOptions[property];
            if (typeof id !== 'string' || !source[id]) {
                throw new Error(`Invalid dfs ${property} config value requested ${id}`);
            }
        }

        switch (property) {
            case 'color':
                return `--dfs-c-${id}`;
        }

        throw new Error(`Invalid dfs config property requested: ${property}`);
    }
}
